import React, { useRef, useEffect } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useCommandHistory } from '../../hooks/useCommandHistory';
import { cn } from '../../utils/cn';

interface Props {
  placeholder?: string;
  disabled?: boolean;
}

export const ConsoleCommandInput: React.FC<Props> = ({
  placeholder = 'Execute JavaScript...',
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { value, onChange, onKeyDown, isExecuting } = useCommandHistory();

  // Focus input when clicking anywhere in the container
  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 border-t border-zinc-700 bg-zinc-900 cursor-text flex-shrink-0"
      onClick={handleContainerClick}
    >
      {/* Prompt indicator */}
      {isExecuting ? (
        <Loader2 size={14} className="text-blue-400 animate-spin flex-shrink-0" />
      ) : (
        <ChevronRight size={14} className="text-blue-400 flex-shrink-0" />
      )}

      {/* Input field */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled || isExecuting}
        className={cn(
          'flex-1 bg-transparent text-sm font-mono text-zinc-300 placeholder:text-zinc-600',
          'focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed'
        )}
        spellCheck={false}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
      />

      {/* Keyboard hints */}
      <div className="flex items-center gap-2 text-xs text-zinc-600 flex-shrink-0">
        <span>History</span>
        <span>Enter</span>
      </div>
    </div>
  );
};
